import { useContext } from "react";

import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { CreateEntityResponse } from "../common/CreateEntityResponse";
import { CreateLanguageRequest } from "./CreateLanguageRequest";
import { DubbingsResponse } from "./DubbingsResponse";
import { DynamicSubtitlesResponse } from "./DynamicSubtitlesResponse";
import { EmbeddedSubtitlesResponse } from "./EmbeddedSubtitlesResponse";
import { GetAirlineListResponse } from "./GetAirlineListResponse";
import { GetAirlinesLookupResponse } from "./GetAirlinesLookupResponse";
import { GetLanguageListResponse } from "./GetLanguageListResponse";
import { GetLanguagesLookupResponse } from "./GetLanguagesLookupResponse";
import { GetUserListResponse } from "./GetUserListResponse";
import { UpdateUserRequest } from "./UpdateUserRequest";

export const useAdminApi = () => {
    const { httpGet, httpPost } = useAuthorizedFetch();

    const appConfigContext = useContext(AppConfigContext);
    const baseUrl = `${appConfigContext.adminConfig.apiUrl}`;

    const getLanguageList = async (): Promise<GetLanguageListResponse> => {
        return httpGet<GetLanguageListResponse>(`${baseUrl}languages/list`);
    };

    const createLanguage = async (request: CreateLanguageRequest): Promise<CreateEntityResponse> => {
        return httpPost(`${baseUrl}languages/create`, request);
    };

    const getLanguagesLookup = async (): Promise<GetLanguagesLookupResponse> => {
        return httpGet<GetLanguagesLookupResponse>(`${baseUrl}languages/get-lookup`);
    };

    const getEmbeddedSubtitles = async (): Promise<EmbeddedSubtitlesResponse> => {
        return httpGet<EmbeddedSubtitlesResponse>(`${baseUrl}languages/embedded-subs-lookup`);
    };

    const getDynamicSubtitles = async (): Promise<DynamicSubtitlesResponse> => {
        return httpGet<DynamicSubtitlesResponse>(`${baseUrl}languages/dynamic-subs-lookup`);
    };

    const getDubbings = async (): Promise<DubbingsResponse> => {
        return httpGet<DubbingsResponse>(`${baseUrl}languages/dubbings-lookup`);
    };

    const getAirlineList = async (): Promise<GetAirlineListResponse> => {
        return httpGet<GetAirlineListResponse>(`${baseUrl}airlines/list`);
    };

    const getAirlinesLookup = async (): Promise<GetAirlinesLookupResponse> => {
        return httpGet<GetLanguagesLookupResponse>(`${baseUrl}airlines/get-lookup`);
    };

    const syncAirlines = async (): Promise<void> => {
        return httpPost(`${baseUrl}airlines/sync`);
    };

    const getUsersList = async (): Promise<GetUserListResponse> => {
        return httpGet<GetUserListResponse>(`${baseUrl}users/list`);
    };

    const updateUser = async (request: UpdateUserRequest): Promise<void> => {
        return httpPost<void>(`${baseUrl}users/update`, request);
    };

    return {
        getLanguageList,
        getLanguagesLookup,
        getAirlineList,
        getAirlinesLookup,
        syncAirlines,
        createLanguage,
        getUsersList,
        updateUser,
        getEmbeddedSubtitles,
        getDynamicSubtitles,
        getDubbings
    };
};
